// BulkChangeModal — change many work items at once: Edit or Delete.
//
//   Edit     The server groups the selection by what an edit depends on —
//            project + work type (the edit SCREEN: which fields, which
//            options) and workflow + current status (which TRANSITIONS) —
//            POST /api/issues/bulk-edit/plan. One card per group: its items,
//            a Status picker offering only the transitions valid for every
//            item in it, then every field bulk edit can write — the item
//            view's fields, then the rest of the edit screen — each an open
//            row where blank means "leave as is" (Parent included when the
//            Architectural Hierarchy gives the type one; a chosen parent is
//            checked at once, refusals per item). A group left alone stays
//            as it is. Writes reuse the guarded
//            routes, per group: bulk-edit (fields) → bulk-parent → bulk-
//            transition (status; a transition with a screen collects it once
//            for the group, no prefill).
//   Delete   The server plans first (permission per project, and a parent
//            whose children are NOT selected is blocked — the window offers
//            to add them, or to leave the blocked items out). Then a confirm,
//            and done. (Deleted items wait in the server's Recycle bin, where
//            a site admin can recover one — the person deleting is not asked
//            about it, kept basic on purpose, 2026-10-06.)
//
// Both end in a confirm (a plain sentence per change) and a result
// (what changed, and each failure with its reason). A selection larger
// than the server's 500 keys per call goes in slices — bulkRuns.mjs.

import { useEffect, useMemo, useRef, useState } from 'react';
import api, { extractErrorMessage } from '@app/services/api';
import { API_BASE } from '@platform/http/api-client';
import UserAvatar from '@platform/ui/UserAvatar';
import SearchableSelect from '@platform/ui/SearchableSelect';
import DateField from '@modules/database/work-items/frontend/editors/DateField';
import MultiSelectEditor from '@modules/database/work-items/frontend/editors/MultiSelectEditor';
import '@modules/database/work-items/frontend/editors/FieldEditor.css';
import TransitionScreenModal from '@modules/database/work-items/frontend/components/TransitionScreenModal/TransitionScreenModal';
import { runInChunks, eachSlice, mergeParentPlans, parentSearchZql, parentCandidatesZql, mergeDeletePlans, mergeEditPlans, groupWrites, hasWrites, familiesOf, chosenTransitions, touchedKeys } from '@modules/database/work-items/frontend/components/BulkChangeModal/bulkRuns.mjs';
import '@modules/database/work-items/frontend/components/BulkChangeModal/BulkChangeModal.css';

// Map a jiraFields entry to a coarse editor type for the value input.
// Returns null when we don't yet support bulk editing this type.
function classifyField(field) {
  if (!field || !field.schema) return null;
  const t = field.schema.type;
  const items = field.schema.items;
  const sys = field.schema.system;
  const custom = field.schema.custom || '';

  // Hard-skipped fields that don't fit a "set field value" model.
  if (sys === 'status' || sys === 'attachment' || sys === 'comment'
    || sys === 'worklog' || sys === 'description'
    || sys === 'timetracking' || sys === 'subtasks' || sys === 'issuelinks'
    || sys === 'progress' || sys === 'votes' || sys === 'watches'
    || sys === 'project' || sys === 'issuetype' || sys === 'parent') return null;

  if (sys === 'assignee' || sys === 'reporter' || t === 'user') return 'user';
  // Priority / resolution have no write path on the server yet (every
  // item would fail) — not offered until they do.
  if (sys === 'priority' || sys === 'resolution' || t === 'priority' || t === 'resolution') return null;
  if (sys === 'labels' || (t === 'array' && items === 'string')) return 'labels';
  if (t === 'date') return 'date';
  if (t === 'datetime') return 'datetime';
  if (t === 'number') return 'number';
  if (t === 'string') return 'text';
  // A multi-select first: its custom type ("…:multiselect") contains
  // "select" too, and read as a single select it sent ONE { id }, which was
  // stored as a single value (Assigned Resource, 2026-10-21).
  if (t === 'array' && items === 'option') return 'multiselect-custom';
  if (t === 'option' || custom.includes('select')) return 'select-custom';
  return null; // unsupported type — surface a friendly message
}

// CLEAR — a row's value that EMPTIES the field on every work item in the
// group, where a blank row leaves it as it is. Sent as null: the write path
// clears a field on null exactly as a single work item's edit does. Never
// offered on a required field.
const CLEAR = Object.freeze({ __clear: true });
const isClear = (v) => !!(v && v.__clear === true);

// Avatar paths from the API are relative to the backend (UserAccountMenu
// resolves them the same way).
function resolvePicture(picture) {
  if (!picture) return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  if (picture.startsWith('/')) return `${API_BASE}${picture}`;
  return picture;
}

// A status as Zebra Search shows it: a lozenge in its category's colour.
const STATUS_BG = { green: 'var(--success)', yellow: '#4a90d9', 'blue-gray': '#6b7280' };
function StatusLozenge({ name, color }) {
  return <span className="bulk-status-lozenge" style={{ backgroundColor: STATUS_BG[color] || 'var(--accent)' }}>{name || '—'}</span>;
}

// A work type, as the app shows it everywhere: its icon and its name.
function TypeBadge({ type }) {
  if (!type) return null;
  return (
    <span className="bulk-type-badge">
      {type.iconUrl && <img src={api.getJiraImageUrl(type.iconUrl)} alt="" className="bulk-group-icon" />}
      <span>{type.name}</span>
    </span>
  );
}

// Choosing a work type (moving to another project): a list with icons.
function TypePicker({ types, value, onChange, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  const chosen = types.find(t => t.id === value) || null;
  return (
    <div className="bulk-status-picker" ref={ref}>
      <button type="button" className="bulk-status-trigger" onClick={() => setOpen(o => !o)} disabled={disabled} aria-haspopup="listbox" aria-expanded={open}>
        {chosen ? <TypeBadge type={chosen} /> : <span className="bulk-status-keep">{placeholder}</span>}
        <span className="bulk-status-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="bulk-status-menu" role="listbox">
          {types.map(t => (
            <button key={t.id} type="button" role="option" aria-selected={t.id === value} className="bulk-status-option" onClick={() => { onChange(t.id); setOpen(false); }}>
              <TypeBadge type={t} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// The Status row: the group's status, and the moves open to every item in
// it — each named by its transition and shown as the status it lands in.
function StatusPicker({ group, chosen, onChoose, onFillScreen, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [open]);
  const pick = (t) => { onChoose(t); setOpen(false); };
  if (!group.transitions.length) {
    return (
      <div className="bulk-status-static">
        <StatusLozenge name={group.statusName} color={group.statusColor} />
        <span className="bulk-edit-value-hint-small">No status change is open to every item here.</span>
      </div>
    );
  }
  return (
    <div className="bulk-status-picker" ref={ref}>
      <button type="button" className="bulk-status-trigger" onClick={() => setOpen(o => !o)} disabled={disabled} aria-haspopup="listbox" aria-expanded={open}>
        <StatusLozenge name={group.statusName} color={group.statusColor} />
        {chosen ? (
          <>
            <span className="bulk-status-arrow" aria-hidden="true">→</span>
            <StatusLozenge name={chosen.toName} color={chosen.toColor} />
            <span className="bulk-status-via">via {chosen.name}</span>
          </>
        ) : <span className="bulk-status-keep">Leave as is</span>}
        <span className="bulk-status-caret" aria-hidden="true">▾</span>
      </button>
      {chosen?.hasScreen && (
        <button type="button" className="bulk-change-screen-btn" disabled={disabled} onClick={onFillScreen}>
          {chosen.fields ? 'Screen filled — edit' : 'Fill in screen…'}
        </button>
      )}
      {open && (
        <div className="bulk-status-menu" role="listbox">
          <button type="button" role="option" aria-selected={!chosen} className="bulk-status-option" onClick={() => pick(null)}>
            <span className="bulk-status-tname">Leave as is</span>
            <StatusLozenge name={group.statusName} color={group.statusColor} />
          </button>
          {group.transitions.map(t => (
            <button key={t.id} type="button" role="option" aria-selected={chosen?.transitionId === String(t.id)} className="bulk-status-option" onClick={() => pick(t)}>
              <span className="bulk-status-tname">{t.name}</span>
              <span className="bulk-status-arrow" aria-hidden="true">→</span>
              <StatusLozenge name={t.to?.name || t.name} color={t.to?.statusCategory?.colorName} />
              {t.hasScreen && <span className="bulk-status-via">asks for a screen</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// `scope` = { projectKey, issueTypeId } of the group being edited: a custom
// user field then searches only its context's allowed users.
function ValueEditor({ field, value, onChange, disabled, scope = null }) {
  const kind = classifyField(field);
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  // Lazy-load select options once per field.
  useEffect(() => {
    let cancelled = false;
    if (!field) { setOptions(null); return; }
    if (kind === 'select-system') {
      const load = async () => {
        setOptionsLoading(true);
        try {
          if (field.schema?.system === 'priority' || field.schema?.type === 'priority') {
            const list = await api.getPriorities();
            if (!cancelled) setOptions((list || []).map(o => ({ id: o.id, label: o.name })));
          } else if (field.schema?.system === 'resolution' || field.schema?.type === 'resolution') {
            const list = await api.getResolutions();
            if (!cancelled) setOptions((list || []).map(o => ({ id: o.id, label: o.name })));
          }
        } catch { if (!cancelled) setOptions([]); }
        finally { if (!cancelled) setOptionsLoading(false); }
      };
      load();
    } else if ((kind === 'select-custom' || kind === 'multiselect-custom') && Array.isArray(field.allowedValues) && field.allowedValues.length) {
      // The group's edit screen already sent this field's options.
      setOptions(field.allowedValues.map(o => ({ id: String(o.id), label: o.value || o.name || String(o.id) })));
    } else if (kind === 'select-custom' || kind === 'multiselect-custom') {
      const load = async () => {
        setOptionsLoading(true);
        try {
          const result = await api.getFieldOptions(field.id);
          const list = Array.isArray(result) ? result : (result?.values || result?.allowedValues || []);
          if (!cancelled) setOptions(list.map(o => ({ id: o.id || o.value, label: o.value || o.name || String(o.id) })));
        } catch { if (!cancelled) setOptions([]); }
        finally { if (!cancelled) setOptionsLoading(false); }
      };
      load();
    } else {
      setOptions(null);
    }
    return () => { cancelled = true; };
  }, [field, kind]);

  // Debounced people search for user fields — only once something is typed
  // (an empty search listed every account, apps first), and people only:
  // app / integration accounts are never someone's assignee.
  useEffect(() => {
    if (kind !== 'user') return;
    if (!userQuery.trim()) { setUserResults([]); setUserLoading(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setUserLoading(true);
      try {
        const fieldScope = String(field.id).startsWith('customfield_') && scope
          ? { fieldId: field.id, projectKey: scope.projectKey, issueTypeId: scope.issueTypeId } : null;
        const list = await api.getUsers(userQuery, fieldScope);
        const people = (Array.isArray(list) ? list : []).filter(u => u && u.accountType !== 'app' && u.active !== false);
        if (!cancelled) setUserResults(people.slice(0, 12));
      } catch { if (!cancelled) setUserResults([]); }
      finally { if (!cancelled) setUserLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [userQuery, kind, field?.id, scope?.projectKey, scope?.issueTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!field) {
    return <div className="bulk-edit-value-hint">Pick a field above to enter its new value.</div>;
  }
  if (kind === null) {
    return (
      <div className="bulk-edit-value-unsupported">
        <strong>{field.name}</strong> isn't supported by bulk edit. Use single-issue editing for this field.
      </div>
    );
  }

  switch (kind) {
    case 'text':
      return (
        <input type="text" className="bulk-edit-value-input"
          value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled} placeholder="Leave as is" />
      );
    case 'number':
      return (
        <input type="number" className="bulk-edit-value-input"
          value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} disabled={disabled} />
      );
    case 'date':
      // Stride's calendar, not the browser's: moving months writes nothing.
      return (
        <DateField className="bulk-edit-value-input" placeholder="Leave as is"
          value={value ?? ''} onChange={(d) => onChange(d || null)} disabled={disabled} />
      );
    case 'datetime':
      return (
        <input type="datetime-local" className="bulk-edit-value-input"
          value={value ?? ''} onChange={e => onChange(e.target.value || null)} disabled={disabled} />
      );
    case 'labels':
      return (
        <div>
          <input type="text" className="bulk-edit-value-input"
            value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled} placeholder="Leave as is" />
          {value && <div className="bulk-edit-value-hint-small">Comma-separated; replaces the existing labels.</div>}
        </div>
      );
    case 'multiselect-custom': {
      // Ticks, as the work item screen picks several values. Nothing ticked
      // = leave as is (Clear empties the field).
      if (optionsLoading) return <div className="bulk-edit-value-hint">Loading options…</div>;
      if (!options || options.length === 0) return <div className="bulk-edit-value-hint-small">No options to choose from.</div>;
      const picked = Array.isArray(value) ? value : [];
      return (
        <div>
          <MultiSelectEditor
            options={options.map(o => ({ id: String(o.id), name: o.label }))}
            value={picked.map(o => ({ id: String(o.id), name: o.label }))}
            onChange={(arr) => {
              const next = (arr || []).map(a => options.find(o => String(o.id) === String(a.id))).filter(Boolean);
              onChange(next.length ? next : null);
            }}
            emptyLabel="Leave as is"
            disabled={disabled}
            collapsible
          />
          {picked.length > 0 && <div className="bulk-edit-value-hint-small">Replaces the existing values with {picked.length === 1 ? 'this one' : `these ${picked.length}`}.</div>}
        </div>
      );
    }
    case 'select-system':
    case 'select-custom': {
      // Searchable, as the work item screen picks a value: type to filter.
      if (optionsLoading) return <div className="bulk-edit-value-hint">Loading options…</div>;
      if (!options || options.length === 0) return <div className="bulk-edit-value-hint-small">No options to choose from.</div>;
      return (
        <div>
          <SearchableSelect
            options={options.map(o => ({ id: String(o.id), name: o.label }))}
            value={value?.id != null ? String(value.id) : null}
            onChange={(id) => onChange(id ? (options.find(o => String(o.id) === String(id)) || null) : null)}
            noneLabel="Leave as is"
            placeholder="Leave as is"
            disabled={disabled}
          />
        </div>
      );
    }
    case 'user':
      if (value) {
        return (
          <div className="bulk-user-chosen">
            <UserAvatar user={value} size={22} resolveImageUrl={resolvePicture} />
            <span className="bulk-user-name">{value.displayName || value.emailAddress || value.accountId}</span>
            <button type="button" className="bulk-edit-user-clear" onClick={() => onChange(null)} disabled={disabled}>Change</button>
          </div>
        );
      }
      return (
        <div>
          <input type="text" className="bulk-edit-value-input"
            placeholder="Leave as is — type a name to change"
            value={userQuery}
            onChange={e => setUserQuery(e.target.value)}
            disabled={disabled} />
          {userQuery.trim() && (
            <div className="bulk-edit-user-results">
              {userLoading && <div className="bulk-edit-user-loading">Searching…</div>}
              {!userLoading && userResults.length === 0 && <div className="bulk-edit-user-empty">No one matches</div>}
              {userResults.map(u => (
                <button key={u.accountId} type="button" className="bulk-edit-user-item bulk-user-option"
                  onClick={() => { onChange(u); setUserQuery(''); setUserResults([]); }}
                  disabled={disabled}>
                  <UserAvatar user={u} size={20} resolveImageUrl={resolvePicture} />
                  <span className="bulk-user-name">{u.displayName || u.emailAddress || u.accountId}</span>
                  {u.emailAddress && <span className="bulk-edit-user-item-email">{u.emailAddress}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    default:
      return null;
  }
}

// Convert the modal's value into the field-shape Jira's PUT expects.
function valueForApi(field, kind, value) {
  if (value === '' || value === undefined || isClear(value)) return null;
  switch (kind) {
    case 'text': return value;
    case 'number': return value === null ? null : Number(value);
    case 'date': return value || null;
    case 'datetime': return value || null;
    case 'labels': {
      const arr = String(value || '').split(',').map(s => s.trim()).filter(Boolean);
      return arr;
    }
    case 'select-system': return value ? { id: String(value.id) } : null;
    case 'select-custom': return value ? { id: String(value.id) } : null;
    case 'multiselect-custom': return (Array.isArray(value) ? value : [value]).filter(Boolean).map(o => ({ id: String(o.id) }));
    case 'user': return value ? { accountId: value.accountId } : null;
    default: return value;
  }
}

// ---------------------------------------------------------------------------
// Set parent — search any work item, or take the items out from under one.
// ---------------------------------------------------------------------------
// projectKey + validTypes (from the group, or the project it moves to) turn
// the search into a LIST: the valid parents show at once, typing narrows.
// Without them (no hierarchy known) it is a plain site-wide search.
function ParentChooser({ mode, onMode, parent, onParent, selectedSet, disabled, hideModes = false, projectKey = null, validTypes = null }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const listed = !!projectKey;
  const typesKey = JSON.stringify(validTypes || null);

  useEffect(() => {
    const zql = listed ? parentCandidatesZql({ projectKey, types: validTypes, text: query }) : parentSearchZql(query);
    if (!zql || parent) { setResults([]); setSearchError(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true); setSearchError(null);
      try {
        const res = await api.searchIssues(zql, 'summary,issuetype,status,project', listed ? 50 : 12, null, 0, true);
        if (!cancelled) setResults((res?.issues || []).filter(i => !selectedSet.has(i.key)));
      } catch (e) {
        if (!cancelled) { setResults([]); setSearchError(extractErrorMessage(e, 'Search failed')); }
      } finally { if (!cancelled) setSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, parent, selectedSet, projectKey, typesKey]);

  return (
    <div className="bulk-parent">
      {!hideModes && <div className="bulk-parent-modes" role="radiogroup" aria-label="Parent change">
        <label className={`bulk-parent-mode ${mode === 'set' ? 'active' : ''}`}>
          <input type="radio" name="bulk-parent-mode" checked={mode === 'set'} onChange={() => onMode('set')} disabled={disabled} />
          Put them under a parent
        </label>
        <label className={`bulk-parent-mode ${mode === 'remove' ? 'active' : ''}`}>
          <input type="radio" name="bulk-parent-mode" checked={mode === 'remove'} onChange={() => onMode('remove')} disabled={disabled} />
          Take them out from under their parent
        </label>
      </div>}
      {mode === 'set' && (
        parent ? (
          <div className="bulk-parent-chosen">
            <span className="bulk-parent-key">{parent.key}</span>
            <span className="bulk-parent-summary">{parent.fields?.summary || ''}</span>
            {parent.fields?.issuetype?.name && <span className="bulk-parent-type">{parent.fields.issuetype.name}</span>}
            <button type="button" className="bulk-edit-user-clear" onClick={() => { onParent(null); setQuery(''); }} disabled={disabled}>Change</button>
          </div>
        ) : (
          <div>
            <input
              type="text" className="bulk-edit-value-input" autoFocus
              placeholder={listed ? `Filter the ${validTypes?.length ? validTypes.join(' / ') : 'work items'} in ${projectKey}…` : 'Key or words from the summary…'}
              value={query} onChange={e => setQuery(e.target.value)} disabled={disabled}
            />
            {(listed || searching || searchError || results.length > 0 || (query && !searching)) && (
              <div className="bulk-edit-user-results">
                {searching && <div className="bulk-edit-user-loading">{listed && !query ? 'Finding the valid parents…' : 'Searching…'}</div>}
                {searchError && <div className="bulk-edit-user-empty">{searchError}</div>}
                {!searching && !searchError && results.length === 0 && (query || listed) && (
                  <div className="bulk-edit-user-empty">{listed ? `No ${validTypes?.length ? validTypes.join(' / ') : 'work item'} in ${projectKey}${query ? ' matches' : ''}` : 'No work items match'}</div>
                )}
                {results.map(i => (
                  <button key={i.key} type="button" className="bulk-edit-user-item bulk-parent-result" onClick={() => onParent(i)} disabled={disabled}>
                    {i.fields?.issuetype?.iconUrl && <img src={api.getJiraImageUrl(i.fields.issuetype.iconUrl)} alt="" className="bulk-group-icon" />}
                    <span className="bulk-parent-key">{i.key}</span>
                    <span className="bulk-parent-summary">{i.fields?.summary || ''}</span>
                    {i.fields?.status?.name && <StatusLozenge name={i.fields.status.name} color={i.fields.status.statusCategory?.colorName} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

function ParentPlanView({ plan, loading, error, parentKey }) {
  if (loading) return <div className="bulk-edit-value-hint">Checking every selected item against the hierarchy…</div>;
  if (error) return <div className="bulk-edit-error">{error}</div>;
  if (!plan) return null;
  if (plan.error) return <div className="bulk-edit-error">{plan.error}</div>;
  const n = (k) => `${k} item${k !== 1 ? 's' : ''}`;
  return (
    <div className="bulk-parent-plan">
      <div className="bulk-parent-counts">
        <span className="bulk-parent-count ok">{n(plan.willChange.length)} will change</span>
        {plan.alreadyThere.length > 0 && <span className="bulk-parent-count same">{n(plan.alreadyThere.length)} {parentKey ? `already under ${parentKey}` : 'already without a parent'}</span>}
        {plan.refused.length > 0 && <span className="bulk-parent-count no">{n(plan.refused.length)} can't</span>}
      </div>
      {plan.refused.length > 0 && (
        <div className="bulk-edit-incompatible-list">
          {plan.refused.slice(0, 20).map(x => (
            <div key={x.key} className="bulk-edit-incompatible-row">
              <span className="bulk-edit-incompatible-key">{x.key}</span>
              <span className="bulk-edit-incompatible-reason">{x.reason}</span>
            </div>
          ))}
          {plan.refused.length > 20 && <div className="bulk-edit-incompatible-more">…and {plan.refused.length - 20} more</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete — what goes to the Recycle bin, what is blocked by its children.
// ---------------------------------------------------------------------------
function DeletePlanView({ plan, loading, error, onAddChildren, skipBlocked, onSkipBlocked, disabled }) {
  if (loading) return <div className="bulk-edit-value-hint">Checking permissions and children…</div>;
  if (error) return <div className="bulk-edit-error">{error}</div>;
  if (!plan) return null;
  const n = (k) => `${k} work item${k !== 1 ? 's' : ''}`;
  return (
    <div className="bulk-parent-plan">
      <div className="bulk-parent-counts">
        <span className="bulk-parent-count ok">{n(plan.deletable.length)} can be deleted</span>
        {plan.blocked.length > 0 && <span className="bulk-parent-count no">{n(plan.blocked.length)} still {plan.blocked.length === 1 ? 'has' : 'have'} children</span>}
        {plan.refused.length > 0 && <span className="bulk-parent-count no">{n(plan.refused.length)} not yours to delete</span>}
      </div>
      {plan.blocked.length > 0 && (
        <div className="bulk-delete-blocked">
          <p className="bulk-edit-value-hint-small">
            Deleting a parent on its own would leave its children without one. Add the children to the selection to delete the whole branch, or leave these parents out.
          </p>
          <div className="bulk-edit-incompatible-list">
            {plan.blocked.slice(0, 20).map(b => (
              <div key={b.key} className="bulk-edit-incompatible-row">
                <span className="bulk-edit-incompatible-key">{b.key}</span>
                <span className="bulk-edit-incompatible-reason">
                  {b.children.length} child{b.children.length !== 1 ? 'ren' : ''} not selected: {b.children.slice(0, 6).join(', ')}{b.children.length > 6 ? ', …' : ''}
                </span>
              </div>
            ))}
            {plan.blocked.length > 20 && <div className="bulk-edit-incompatible-more">…and {plan.blocked.length - 20} more</div>}
          </div>
          <div className="bulk-delete-choices">
            {onAddChildren && (
              <button type="button" className="bulk-edit-btn-secondary" onClick={() => onAddChildren(plan.childrenToAdd)} disabled={disabled}>
                Add the {plan.childrenToAdd.length} child{plan.childrenToAdd.length !== 1 ? 'ren' : ''} to the selection
              </button>
            )}
            <label className="bulk-parent-mode">
              <input type="checkbox" checked={skipBlocked} onChange={e => onSkipBlocked(e.target.checked)} disabled={disabled} />
              Leave these {plan.blocked.length} out and delete the rest
            </label>
          </div>
        </div>
      )}
      {plan.refused.length > 0 && (
        <div className="bulk-edit-incompatible-list">
          {plan.refused.slice(0, 20).map(x => (
            <div key={x.key} className="bulk-edit-incompatible-row">
              <span className="bulk-edit-incompatible-key">{x.key}</span>
              <span className="bulk-edit-incompatible-reason">{x.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Human sentence(s) for the confirm step.
function describeEdit(field, kind, value) {
  if (isClear(value)) return `Clear ${field?.name || field?.id}`;
  const shown = (() => {
    if (value == null || value === '') return '(empty)';
    if (kind === 'user') return value.displayName || value.emailAddress || value.accountId;
    if (kind === 'multiselect-custom') return (Array.isArray(value) ? value : [value]).map(o => o.label || o.id).join(', ');
    if (kind === 'select-system' || kind === 'select-custom') return value.label || value.id;
    return String(value);
  })();
  return `Set ${field?.name || field?.id} to "${shown}"`;
}

// ---------------------------------------------------------------------------
// One group's card: its items, then rows — Status (only transitions valid
// for every item in it), Parent (when the hierarchy gives the type one) and
// every field bulk edit can write on its screen. Blank = leave as is.
// ---------------------------------------------------------------------------
const SHOW_ITEMS = 8;
// The work item's own layout sections (Settings → screen layouts), in the
// order the item view draws them; edit-screen-only fields come last.
const SECTIONS = [
  { id: 'details', title: 'Details' },
  { id: 'content', title: 'Content' },
  { id: 'other', title: 'More fields' },
];

function GroupCard({ group, edit, onEdit, selectedSet, collapsible, startOpen, attention = false, disabled, onFillScreen, projects = [] }) {
  const [open, setOpen] = useState(startOpen);
  // A group Next found half-filled opens itself so the gap is in view.
  useEffect(() => { if (attention) setOpen(true); }, [attention]);
  const [showAll, setShowAll] = useState(false);
  // The card's work items are folded away until asked for — the rows to
  // fill in come first.
  const [showItems, setShowItems] = useState(false);
  // THE PROJECT'S OWN WORK TYPES, for the Work type row. A different type is
  // a move to this same project (work-item-move.js): the key stays, and the
  // status, parent and fields are decided by the move's own rules.
  const [projectTypes, setProjectTypes] = useState(null);
  useEffect(() => {
    if (!open || group.readOnly || projectTypes) return undefined;
    let on = true;
    api.getProjectIssueTypes(group.projectKey)
      .then((list) => { if (on) setProjectTypes((Array.isArray(list) ? list : []).map(t => ({ id: String(t.id), name: t.name, iconUrl: t.iconUrl || null }))); })
      .catch(() => { if (on) setProjectTypes([]); });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group.projectKey, group.readOnly]);
  // Every field bulk edit can write on this group's screen, each an open
  // row: blank = leave as is, a value = change it on every item here.
  const fields = useMemo(() => {
    const list = edit.move?.plan?.targetFields && !edit.move.plan.error ? edit.move.plan.targetFields : (group.fields || []);
    return list.filter(f => classifyField(f));
  }, [group, edit.move?.plan]);
  const valueOf = (id) => (edit.fields || []).find(r => r.field.id === id)?.value ?? null;
  const setValue = (field, value) => {
    const rest = (edit.fields || []).filter(r => r.field.id !== field.id);
    const blank = value === null || value === undefined || value === '';
    onEdit({ ...edit, fields: blank ? rest : [...rest, { field, value }] });
  };
  const parentMode = edit.parent ? edit.parent.mode : 'keep';
  const setParentMode = (m) => onEdit({ ...edit, parent: m === 'keep' ? null : { mode: m, issue: null, plan: null } });
  const chosenList = chosenTransitions(group, edit);
  const changes = chosenList.length + (edit.fields?.length || 0) + (edit.parent ? 1 : 0) + (edit.move ? 1 : 0);
  const setTransition = (sub, t) => {
    const next = { ...(edit.transitions || {}) };
    if (t) next[sub.id] = { transitionId: String(t.id), name: t.name, toStatusId: t.to?.id || null, toName: t.to?.name || t.name, toColor: t.to?.statusCategory?.colorName || null, hasScreen: !!t.hasScreen, fields: null, comment: null };
    else delete next[sub.id];
    onEdit({ ...edit, transitions: next });
  };
  const readOnlyStatuses = group.statuses.filter(g => g.readOnly);
  const missingNames = (ids) => (ids || []).map(id => group.statuses.find(g => String(g.statusId) === String(id))?.statusName).filter(Boolean);
  const n = group.keys.length;
  const items = showAll ? group.items : group.items.slice(0, SHOW_ITEMS);
  // The project these items are moving to (Project row), or null. A move
  // decides the status and the parent in THAT project: the Status row
  // becomes "Status in X", the parent list comes from X (required when
  // X's hierarchy gives the type a parent).
  const move = edit.move || null;
  const mp = move?.plan || null;
  const target = move ? { projectKey: move.target.projectKey, validParentTypes: mp ? mp.parentTypes : null } : null;
  const moveTo = (projectKey) => onEdit({
    ...edit,
    move: projectKey ? { target: { projectKey }, workTypeId: null, statusId: null, plan: null } : null,
    transitions: {},
    parent: null,
  });
  // A different work type in the same project (null / its own type = none).
  const same = !!move?.sameProject;
  const changeTypeTo = (typeId) => onEdit({
    ...edit,
    move: typeId && String(typeId) !== String(group.workTypeId)
      ? { target: { projectKey: group.projectKey }, sameProject: true, workTypeId: String(typeId), statusId: null, plan: null }
      : null,
    transitions: {},
    parent: null,
  });
  // "Status in ABC" when moving there; just "Status" when only the type changes.
  const inTarget = move && !same ? ` in ${move.target.projectKey}` : '';
  // THE PARENT IT HAS, SHOWN PRE-SELECTED: when the new type may keep the
  // parent the items already share, the Parent row opens on it — seen, and
  // changeable — instead of being kept out of sight. Once per type chosen
  // (move.preselected), so "Leave as is" stays the person's own choice after.
  useEffect(() => {
    if (!same || !mp || mp.error || edit.parent || move.preselected) return;
    const ok = mp.items.filter(i => i.ok);
    const keys = [...new Set(ok.map(i => (i.parentAction === 'keep' ? i.currentParentKey : null)))];
    if (!ok.length || keys.length !== 1 || !keys[0]) return;
    const it = ok[0];
    onEdit({ ...edit, parent: { mode: 'set', issue: { key: it.currentParentKey, fields: { summary: it.currentParentSummary || '' } }, plan: null }, move: { ...move, plan: null, preselected: true } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [same, mp]);
  const parentRequired = !!(move && mp?.parentRequired);
  const showParent = move ? (mp ? (mp.parentTypes === null || mp.parentTypes.length > 0) : false) : (group.parentAllowed && !group.readOnly);
  const otherProjects = projects.filter(p => p.key !== group.projectKey);

  return (
    <section className={`bulk-group${open ? ' is-open' : ''}${attention ? ' needs-attention' : ''}`}>
      <button type="button" className="bulk-group-head" onClick={() => collapsible && setOpen(o => !o)} aria-expanded={open} disabled={!collapsible}>
        {collapsible && (
          <svg className="bulk-group-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points={open ? '6 9 12 15 18 9' : '9 6 15 12 9 18'} />
          </svg>
        )}
        <GroupIdentity group={group} />
        <span className="bulk-group-right">
          {changes > 0 && <span className="bulk-group-changes">{changes} change{changes !== 1 ? 's' : ''}</span>}
          <span className="bulk-group-count">{n} work item{n !== 1 ? 's' : ''}</span>
        </span>
      </button>
      {open && (
        <div className="bulk-group-body">
          <button type="button" className="bulk-see-list bulk-group-items-toggle" onClick={() => setShowItems(v => !v)} aria-expanded={showItems}>
            {showItems ? `Hide the ${n} work item${n !== 1 ? 's' : ''}` : `Show the ${n} work item${n !== 1 ? 's' : ''}`}
          </button>
          {showItems && (
          <div className="bulk-group-items">
            {items.map(it => (
              <div key={it.key} className="bulk-selection-row">
                <span className="bulk-parent-key">{it.key}</span>
                <span className="bulk-parent-summary">{it.summary}</span>
                {group.statuses.length > 1 && <StatusLozenge name={it.statusName} color={it.statusColor} />}
              </div>
            ))}
            {group.items.length > SHOW_ITEMS && (
              <button type="button" className="bulk-see-list bulk-group-seeall" onClick={() => setShowAll(v => !v)}>
                {showAll ? 'See less' : `See all ${group.items.length}`}
              </button>
            )}
          </div>
          )}

          <div className="bulk-field-list">
            {otherProjects.length > 0 && !group.readOnly && (
              <div className={`bulk-field-row${move ? ' is-changed' : ''}`}>
                <span className="bulk-field-name">Project</span>
                <div className="bulk-field-editor">
                  {/* Searchable: a site can have hundreds of projects — type
                      a name or key to find the one to move to. */}
                  <SearchableSelect
                    options={otherProjects.map(p => ({ id: p.key, name: `Move to ${p.name ? `${p.name} (${p.key})` : p.key}` }))}
                    value={move && !same ? move.target.projectKey : null}
                    onChange={(key) => moveTo(key || null)}
                    noneLabel={`Leave in ${group.projectKey}`}
                    placeholder="Search projects by name or key"
                    disabled={disabled}
                  />
                  {move && !same && <MovePlanView move={move} group={group} />}
                </div>
              </div>
            )}

            {/* WORK TYPE in this project — Jira's Move → Change type. */}
            {!group.readOnly && (!move || same) && (
              <div className={`bulk-field-row${same ? ' is-changed' : ''}`}>
                <span className="bulk-field-name">Work type</span>
                <div className="bulk-field-editor">
                  {projectTypes === null ? <div className="bulk-edit-value-hint-small">Loading the work types…</div> : (
                    <TypePicker
                      types={projectTypes} value={same ? move.workTypeId : String(group.workTypeId || '')} disabled={disabled}
                      placeholder={group.workTypeName || 'Choose a work type'}
                      onChange={changeTypeTo}
                    />
                  )}
                  {same && <MovePlanView move={move} group={group} />}
                </div>
              </div>
            )}

            {move && !same && mp && !mp.error && (
              <div className={`bulk-field-row${(move.workTypeId && move.workTypeId !== mp.workTypeId) ? ' is-changed' : ''}`}>
                <span className="bulk-field-name">Work type in {move.target.projectKey}</span>
                <div className="bulk-field-editor">
                  <TypePicker
                    types={mp.workTypes} value={move.workTypeId || mp.workTypeId || null} disabled={disabled}
                    placeholder={`Choose — ${group.workTypeName} is not in ${move.target.projectKey}`}
                    onChange={(id) => onEdit({ ...edit, move: { ...move, workTypeId: id, statusId: null, plan: null }, parent: null })}
                  />
                  {(move.workTypeId && move.workTypeId !== String(group.workTypeId || '')) && (
                    <div className="bulk-edit-value-hint-small">A different work type can run a different workflow and show different fields — the status and fields below are {move.target.projectKey}'s for this type.</div>
                  )}
                </div>
              </div>
            )}

            {move && mp && !mp.error && mp.workTypeId && (
              <div className={`bulk-field-row${move.statusId ? ' is-changed' : ''}`}>
                <span className="bulk-field-name">Status{inTarget}</span>
                <div className="bulk-field-editor">
                  {mp.statusKeep ? (
                    <div className="bulk-status-static">
                      {group.statuses.map(g => <StatusLozenge key={g.id} name={g.statusName} color={g.statusColor} />)}
                      <span className="bulk-edit-value-hint-small">{group.statuses.length > 1 ? 'each stays' : 'stays'} — {same ? 'the new work type\'s' : `${move.target.projectKey}'s`} workflow has {group.statuses.length > 1 ? 'them' : 'it'}</span>
                    </div>
                  ) : (
                    <div className="bulk-change-group-pick">
                      <select className="bulk-edit-value-input" value={move.statusId || ''} disabled={disabled}
                        onChange={e => onEdit({ ...edit, move: { ...move, statusId: e.target.value || null } })}>
                        <option value="">Choose — {missingNames(mp.statusesMissing).join(', ') || 'their status'} {missingNames(mp.statusesMissing).length > 1 ? 'are' : 'is'} not in {same ? 'the new work type\'s' : `${move.target.projectKey}'s`} workflow</option>
                        {mp.statuses.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
                      </select>
                      {move.statusId && (() => { const st = mp.statuses.find(x => x.id === move.statusId); return st ? <StatusLozenge name={st.name} color={st.color} /> : null; })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!move && (
            <div className={`bulk-field-row${chosenList.length ? ' is-changed' : ''}`}>
              <span className="bulk-field-name">Status</span>
              <div className="bulk-field-editor">
                {/* One picker per status the card's items are in — each
                    offers only the moves open from THAT status. */}
                <div className="bulk-status-lines">
                  {group.statuses.map(sub => {
                    const chosen = edit.transitions?.[sub.id] || null;
                    const k = sub.keys.length;
                    return (
                      <div key={sub.id} className="bulk-status-line">
                        {group.statuses.length > 1 && <span className="bulk-status-count" title={`${k} work item${k !== 1 ? 's' : ''} in ${sub.statusName}`}>{k}</span>}
                        <StatusPicker
                          group={sub} chosen={chosen} disabled={disabled}
                          onFillScreen={() => onFillScreen(sub, chosen, group)}
                          onChoose={(t) => setTransition(sub, t)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

            {showParent && (
              <div className={`bulk-field-row${edit.parent ? ' is-changed' : ''}${parentRequired && !edit.parent?.issue ? ' is-required' : ''}`}>
                <span className="bulk-field-name">{`Parent${inTarget}`}{parentRequired && <span className="bulk-required" title="Required"> *</span>}</span>
                <div className="bulk-field-editor">
                  {parentRequired ? (
                    <div className="bulk-edit-value-hint-small">A {mp.workTypes.find(t => t.id === (move.workTypeId || mp.workTypeId))?.name || 'work item'}{inTarget} sits under {Array.isArray(mp.parentTypes) && mp.parentTypes.length ? `a ${mp.parentTypes.join(' or ')}` : 'a parent'} — choose one:</div>
                  ) : (
                    <select className="bulk-edit-value-input" value={parentMode} disabled={disabled} onChange={e => setParentMode(e.target.value)}>
                      <option value="keep">Leave as is</option>
                      <option value="set">Change parent</option>
                    </select>
                  )}
                  {(edit.parent?.mode === 'set' || parentRequired) && (
                    <ParentChooser
                      mode="set" hideModes onMode={() => {}}
                      parent={edit.parent?.issue || null} onParent={(issue) => onEdit({ ...edit, parent: { mode: 'set', issue, plan: null }, ...(move ? { move: { ...move, plan: null } } : {}) })}
                      selectedSet={selectedSet} disabled={disabled}
                      projectKey={target?.projectKey || group.projectKey}
                      validTypes={target ? target.validParentTypes : group.validParentTypes}
                    />
                  )}
                  {edit.parent && !move && <ParentPlanView plan={edit.parent.plan} loading={edit.parent.planLoading} error={edit.parent.planError} parentKey={edit.parent.mode === 'set' ? edit.parent.issue?.key : null} />}
                </div>
              </div>
            )}

            {SECTIONS.map(sec => {
              const list = fields.filter(f => (f.section || 'other') === sec.id);
              if (!list.length) return null;
              return (
                <div key={sec.id} className="bulk-field-section">
                  <div className="bulk-field-section-title">{sec.title}</div>
                  {list.map(f => {
                    const v = valueOf(f.id);
                    const cleared = isClear(v);
                    return (
                      <div key={f.id} className={`bulk-field-row${v !== null ? ' is-changed' : ''}`}>
                        <span className="bulk-field-name">{f.name}</span>
                        {/* Just left of the box, in a column of its own, so every
                            box starts and ends in one line whether it has one or not. */}
                        {!f.required && !cleared && (
                          <button type="button" className="bulk-field-clear" onClick={() => setValue(f, CLEAR)} disabled={disabled}
                            title={`Empty ${f.name} on every work item here`}>Clear</button>
                        )}
                        <div className="bulk-field-editor">
                          {cleared ? (
                            <div className="bulk-field-cleared">
                              Will be cleared
                              <button type="button" className="bulk-edit-user-clear" onClick={() => setValue(f, null)} disabled={disabled}>Undo</button>
                            </div>
                          ) : (
                            <ValueEditor field={f} value={v} onChange={(nv) => setValue(f, nv)} disabled={disabled} scope={{ projectKey: target?.projectKey || group.projectKey, issueTypeId: move?.workTypeId || group.workTypeId }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {readOnlyStatuses.length > 0 && (
            <div className="bulk-edit-value-hint-small">
              {group.readOnly
                ? `${readOnlyStatuses.map(g => g.statusName).join(', ')} ${readOnlyStatuses.length > 1 ? 'are' : 'is'} read-only for you — only the status can change.`
                : `Work items in ${readOnlyStatuses.map(g => g.statusName).join(', ')} (read-only for you) keep their fields, parent and project — only their status can change.`}
            </div>
          )}
          {!group.readOnly && fields.length === 0 && (
            <div className="bulk-edit-value-hint-small">Nothing else on this group's screen can be changed in bulk{group.parentAllowed ? ' besides the parent' : ''} — Summary and Description are changed per work item.</div>
          )}
        </div>
      )}
    </section>
  );
}

// "a Task", "an Epic".
const an = (name) => `${/^[aeiou]/i.test(String(name || '')) ? 'an' : 'a'} ${name}`;

// What a move will do, from the server's plan: which items go, which come
// along (sub-tasks / children), and which cannot — each with its reason.
function MovePlanView({ move, group }) {
  const same = !!move.sameProject;
  if (move.planLoading) return <div className="bulk-edit-value-hint-small">Checking {same ? 'the new work type' : move.target.projectKey}: workflow, hierarchy, permissions…</div>;
  if (move.planError) return <div className="bulk-edit-error">{move.planError}</div>;
  const mp = move.plan;
  if (!mp) return null;
  if (mp.error) return <div className="bulk-edit-error">{mp.error}</div>;
  const ok = mp.items.filter(i => i.ok);
  const no = [...mp.items.filter(i => !i.ok), ...(mp.refused || [])];
  const along = ok.flatMap(i => i.descendants || []);
  const newType = mp.workTypes.find(t => t.id === (move.workTypeId || mp.workTypeId))?.name || 'the new work type';
  const losing = ok.filter(i => i.parentAction === 'remove');
  const rehung = ok.filter(i => i.rehang);
  return (
    <div className="bulk-move-plan">
      <div className="bulk-parent-counts">
        {same
          ? <span className="bulk-parent-count ok">{ok.length} of {group.keys.length} will become {newType} — their keys stay</span>
          : <span className="bulk-parent-count ok">{ok.length} of {group.keys.length} will move — new {move.target.projectKey} keys; the old keys keep working</span>}
        {along.length > 0 && <span className="bulk-parent-count same">{along.length} sub-task{along.length !== 1 ? 's' : ''} / child{along.length !== 1 ? 'ren' : ''} move with them</span>}
        {no.length > 0 && <span className="bulk-parent-count no">{no.length} can't</span>}
      </div>
      {(mp.fieldsHidden?.length > 0 || mp.fieldsKept?.length > 0) && (
        <div className="bulk-move-fields">
          {mp.fieldsKept?.length > 0 && (
            <div><span className="bulk-move-fields-label">Carry over:</span> {mp.fieldsKept.map(f => f.name).join(', ')}</div>
          )}
          {mp.fieldsHidden?.length > 0 && (
            <div>
              <span className="bulk-move-fields-label">Not on {same ? `${newType}'s` : `${move.target.projectKey}'s`} screen:</span> {mp.fieldsHidden.map(f => `${f.name}${f.items > 1 ? ` (${f.items})` : ''}`).join(', ')}
              <div className="bulk-edit-value-hint-small">Their values are kept in the database, not deleted — they show again if the work item {same ? 'changes back' : 'moves back'} or {same ? 'that' : `${move.target.projectKey}'s`} screen adds the field.</div>
            </div>
          )}
        </div>
      )}
      {losing.length > 0 && (
        <div className="bulk-edit-value-hint-small">
          {losing.length === 1 ? `${losing[0].key} leaves its parent ${losing[0].currentParentKey}` : `${losing.length} leave their parent`} — {an(newType)} has no parent in this project's hierarchy.
        </div>
      )}
      {rehung.length > 0 && (
        <div className="bulk-edit-value-hint-small">
          {rehung.length === 1 ? `${rehung[0].key} keeps its parent ${rehung[0].currentParentKey}` : `${rehung.length} keep their parent`}, held the way {an(newType)} is.
        </div>
      )}
      {along.length > 0 && (
        <div className="bulk-edit-value-hint-small">
          Moving with them: {along.slice(0, 8).map(d => `${d.key}${d.statusName ? ` (${d.statusName})` : ''}`).join(', ')}{along.length > 8 ? ` and ${along.length - 8} more` : ''}.
        </div>
      )}
      {no.length > 0 && (
        <div className="bulk-edit-incompatible-list">
          {no.slice(0, 12).map(x => (
            <div key={x.key} className="bulk-edit-incompatible-row">
              <span className="bulk-edit-incompatible-key">{x.key}</span>
              <span className="bulk-edit-incompatible-reason">{x.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Who a group is: type icon + work type, project chip (and a status
// lozenge where one status is meant — a card holds several).
function GroupIdentity({ group }) {
  return (
    <span className="bulk-group-identity">
      {group.workTypeIconUrl && <img src={api.getJiraImageUrl(group.workTypeIconUrl)} alt="" className="bulk-group-icon" />}
      <span className="bulk-group-type">{group.workTypeName || 'Work item'}</span>
      <span className="bulk-group-project">{group.projectKey || '—'}</span>
      {group.statusName && <StatusLozenge name={group.statusName} color={group.statusColor} />}
    </span>
  );
}

// What a row was set to, in words (and the person's picture for users).
function ShownValue({ field, value }) {
  if (isClear(value)) return <span className="bulk-cleared">Cleared</span>;
  const kind = classifyField(field);
  if (kind === 'user' && value) {
    return <span className="bulk-confirm-user"><UserAvatar user={value} size={20} resolveImageUrl={resolvePicture} />{value.displayName || value.emailAddress || value.accountId}</span>;
  }
  const shown = describeEdit(field, kind, value).replace(/^Set .*? to "/, '').replace(/"$/, '');
  return <span>{shown}</span>;
}

// The confirm step: one block per changed group, one line per change.
function ConfirmGroup({ group, edit }) {
  const n = group.keys.length;
  return (
    <section className="bulk-confirm-group">
      <div className="bulk-confirm-head">
        <GroupIdentity group={group} />
        <span className="bulk-group-count">{n} work item{n !== 1 ? 's' : ''}</span>
      </div>
      <table className="bulk-confirm-table">
        <tbody>
          {edit.move && edit.move.plan && edit.move.sameProject && (() => {
            const mp = edit.move.plan;
            const t = mp.workTypes.find(x => x.id === (edit.move.workTypeId || mp.workTypeId));
            const st = edit.move.statusId ? mp.statuses.find(x => x.id === edit.move.statusId) : null;
            const no = mp.items.filter(i => !i.ok).length + (mp.refused || []).length;
            const losing = mp.items.filter(i => i.ok && i.parentAction === 'remove').length;
            return (
              <tr>
                <th scope="row">Work type</th>
                <td>
                  {group.workTypeName} → <strong>{t?.name || '—'}</strong>
                  <span className="bulk-confirm-note">
                    {st ? <> · status <StatusLozenge name={st.name} color={st.color} /></> : ' · status stays'}
                    {edit.parent?.issue ? ` · under ${edit.parent.issue.key}` : ''}
                    {losing ? ` · ${losing} leave${losing === 1 ? 's' : ''} its parent` : ''}
                    {no ? ` · ${no} can't change and stay` : ''}
                    {' · the keys stay'}
                    {mp.fieldsHidden?.length ? ` · not on its screen (kept): ${mp.fieldsHidden.map(f => f.name).join(', ')}` : ''}
                  </span>
                </td>
              </tr>
            );
          })()}
          {edit.move && edit.move.plan && !edit.move.sameProject && (
            <tr>
              <th scope="row">Project</th>
              <td>
                {group.projectKey} → <strong>{edit.move.target.projectKey}</strong>
                {(() => {
                  const mp = edit.move.plan;
                  const tid = edit.move.workTypeId || mp.workTypeId;
                  const t = mp.workTypes.find(x => x.id === tid);
                  const st = edit.move.statusId ? mp.statuses.find(x => x.id === edit.move.statusId) : null;
                  const along = mp.items.filter(i => i.ok).flatMap(i => i.descendants || []).length;
                  const no = mp.items.filter(i => !i.ok).length + (mp.refused || []).length;
                  return (
                    <span className="bulk-confirm-note">
                      {t && t.name !== group.workTypeName ? ` · as ${t.name}` : ''}
                      {st ? <> · status <StatusLozenge name={st.name} color={st.color} />{group.statuses.length > 1 && !mp.statusKeep ? ` for ${(mp.statusesMissing || []).map(id => group.statuses.find(g => String(g.statusId) === String(id))?.statusName).filter(Boolean).join(', ')}` : ''}</> : ' · status stays'}
                      {edit.parent?.issue ? ` · under ${edit.parent.issue.key}` : ''}
                      {along ? ` · ${along} sub-task${along !== 1 ? 's' : ''}/child${along !== 1 ? 'ren' : ''} move with them` : ''}
                      {no ? ` · ${no} can't move and stay` : ''}
                      {' · new keys; the old ones keep working'}
                      {mp.fieldsHidden?.length ? ` · not shown in ${edit.move.target.projectKey} (kept): ${mp.fieldsHidden.map(f => f.name).join(', ')}` : ''}
                    </span>
                  );
                })()}
              </td>
            </tr>
          )}
          {chosenTransitions(group, edit).map(({ status, transition }) => (
            <tr key={status.id}>
              <th scope="row">Status</th>
              <td>
                <StatusLozenge name={status.statusName} color={status.statusColor} />
                <span className="bulk-status-arrow" aria-hidden="true"> → </span>
                <StatusLozenge name={transition.toName} color={transition.toColor} />
                <span className="bulk-status-via"> via {transition.name}{transition.fields ? ' (screen filled in)' : ''}{group.statuses.length > 1 ? ` · ${status.keys.length} work item${status.keys.length !== 1 ? 's' : ''}` : ''}</span>
              </td>
            </tr>
          ))}
          {edit.parent && !edit.move && (
            <tr>
              <th scope="row">Parent</th>
              <td>
                {edit.parent.mode === 'remove' ? 'Removed' : <><strong>{edit.parent.issue?.key}</strong> {edit.parent.issue?.fields?.summary || ''}</>}
                {edit.parent.plan && edit.parent.plan.refused.length > 0 && (
                  <span className="bulk-confirm-note"> — {edit.parent.plan.refused.length} can't take it and stay as they are</span>
                )}
              </td>
            </tr>
          )}
          {(edit.fields || []).map(r => (
            <tr key={r.field.id}>
              <th scope="row">{r.field.name}</th>
              <td><ShownValue field={r.field} value={r.value} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// What still needs doing before a group's changes can run — said in words.
function problemsOf(group, edit) {
  const out = [];
  if (edit.move && edit.move.sameProject) {
    const mp = edit.move.plan;
    if (edit.move.planLoading || (!mp && !edit.move.planError)) out.push('The new work type is still being checked — a moment.');
    else if (edit.move.planError || mp?.error) out.push(edit.move.planError || mp.error);
    else {
      if (!mp.statusKeep && !edit.move.statusId) out.push('Choose the status — the new work type\'s workflow does not have the current one.');
      if (mp.parentRequired && !edit.parent?.issue) out.push('Choose the parent — the new work type needs one.');
      if (mp.items.length && !mp.items.some(i => i.ok)) out.push('None of these can change to that work type (reasons under Work type).');
    }
    return out;
  }
  if (edit.move) {
    const mp = edit.move.plan;
    if (edit.move.planLoading || (!mp && !edit.move.planError)) out.push(`The move to ${edit.move.target.projectKey} is still being checked — a moment.`);
    else if (edit.move.planError || mp?.error) out.push(edit.move.planError || mp.error);
    else {
      if (!(edit.move.workTypeId || mp.workTypeId)) out.push(`Choose the work type in ${edit.move.target.projectKey}.`);
      else if (!mp.statusKeep && !edit.move.statusId) {
        const names = (mp.statusesMissing || []).map(id => group.statuses.find(g => String(g.statusId) === String(id))?.statusName).filter(Boolean);
        out.push(`Choose the status in ${edit.move.target.projectKey} — ${names.join(', ') || 'their status'} ${names.length > 1 ? 'are' : 'is'} not in its workflow.`);
      }
      if (mp.parentRequired && !edit.parent?.issue) out.push(`Choose the parent in ${edit.move.target.projectKey} — the hierarchy there requires one.`);
      if (mp.items.length && !mp.items.some(i => i.ok)) out.push(`None of these can move to ${edit.move.target.projectKey} (reasons under Project).`);
    }
    return out;
  }
  if (edit.parent?.mode === 'set' && !edit.parent.issue) out.push('Parent is set to "Change parent" but no parent is picked — pick one, or set Parent back to Leave as is.');
  if (edit.parent?.planLoading) out.push('The parent is still being checked — a moment.');
  if (edit.parent?.plan && edit.parent.plan.willChange.length === 0 && edit.parent.plan.alreadyThere.length === 0) out.push('None of these work items can take that parent (reasons under Parent).');
  for (const { status, transition } of chosenTransitions(group, edit)) {
    if (transition.hasScreen && !transition.fields) out.push(`"${transition.name}" (from ${status.statusName}) asks for a screen — use "Fill in screen…".`);
  }
  return out;
}

// Human lines for one group on the confirm step.
function describeGroup(group, edit) {
  const parts = [];
  for (const { status, transition } of chosenTransitions(group, edit)) parts.push(`${status.statusName} → ${transition.toName}`);
  for (const r of edit.fields || []) parts.push(describeEdit(r.field, classifyField(r.field), r.value).replace(/^Set /, ''));
  if (edit.parent?.mode === 'remove') parts.push('no parent');
  else if (edit.parent?.issue) parts.push(`parent → ${edit.parent.issue.key}`);
  return `${group.keys.length} × ${group.workTypeName || 'work item'} in ${group.projectKey}: ${parts.join('; ')}`;
}

const blankEdit = () => ({ transitions: {}, fields: [], parent: null });

// ---------------------------------------------------------------------------
// The window. `op` = 'edit' (default) | 'delete'.
// ---------------------------------------------------------------------------
export default function BulkChangeModal({ open, onClose, selectedKeys, siteUrl, onComplete, op: lockedOp = null, onAddKeys = null, itemsByKey = null }) {
  const op = lockedOp === 'delete' ? 'delete' : 'edit';
  const [step, setStep] = useState('choose'); // 'choose' | 'confirm' | 'running' | 'result'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showList, setShowList] = useState(false);

  // Edit
  const [editPlan, setEditPlan] = useState(null);
  const [editPlanLoading, setEditPlanLoading] = useState(false);
  const [editPlanError, setEditPlanError] = useState(null);
  const [edits, setEdits] = useState({}); // card id → { transitions: { [status group id]: … }, fields, parent, move }
  const [screenFor, setScreenFor] = useState(null); // { group (status), transition, familyId }
  const [attention, setAttention] = useState(() => new Set()); // group ids Next found half-filled
  const [projects, setProjects] = useState([]); // for the Project row

  // Delete
  const [deletePlan, setDeletePlan] = useState(null);
  const [deletePlanLoading, setDeletePlanLoading] = useState(false);
  const [deletePlanError, setDeletePlanError] = useState(null);
  const [skipBlocked, setSkipBlocked] = useState(false);

  const keys = useMemo(() => Array.from(selectedKeys || []), [selectedKeys]);
  const selectedSet = useMemo(() => new Set(keys), [keys]);
  const count = keys.length;

  useEffect(() => {
    if (!open) {
      setStep('choose'); setBusy(false); setError(null); setResult(null); setProgress(null); setShowList(false);
      setEditPlan(null); setEditPlanLoading(false); setEditPlanError(null); setEdits({}); setScreenFor(null);
      setDeletePlan(null); setDeletePlanLoading(false); setDeletePlanError(null); setSkipBlocked(false);
    }
  }, [open]);

  // The projects a group could move to.
  useEffect(() => {
    if (!open || op !== 'edit') return;
    let cancelled = false;
    api.getProjects().then(list => {
      if (!cancelled) setProjects((Array.isArray(list) ? list : []).map(p => ({ key: p.key, name: p.name })).filter(p => p.key).sort((a, b) => String(a.name || a.key).localeCompare(String(b.name || b.key))));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, op]);

  // A chosen move is planned by the server at once — and again when its
  // work type, status or parent changes (each changes what is valid).
  const moveAsks = JSON.stringify(Object.entries(edits).map(([id, e]) => [id, e.move?.target?.projectKey || null, e.move?.workTypeId || null, e.move?.statusId || null, e.parent?.issue?.key || null]));
  useEffect(() => {
    if (!editPlan) return;
    for (const g of familiesOf(editPlan.groups)) {
      const e = edits[g.id];
      const m = e?.move;
      if (!m || m.plan || m.planLoading || m.planError) continue;
      const ask = JSON.stringify([m.target.projectKey, m.workTypeId, m.statusId, e.parent?.issue?.key || null]);
      const settle = (patch) => setEdits(prev => {
        const cur = prev[g.id];
        const cm = cur?.move;
        if (!cm || JSON.stringify([cm.target.projectKey, cm.workTypeId, cm.statusId, cur.parent?.issue?.key || null]) !== ask) return prev;
        return { ...prev, [g.id]: { ...cur, move: { ...cm, planLoading: false, ...patch } } };
      });
      setEdits(prev => ({ ...prev, [g.id]: { ...prev[g.id], move: { ...prev[g.id].move, planLoading: true } } }));
      api.planBulkMove(g.editableKeys.slice(0, 500), m.target.projectKey, { workTypeId: m.workTypeId, statusId: m.statusId, parentKey: e.parent?.issue?.key || null })
        .then(plan => settle({ plan }))
        .catch(err => settle({ planError: extractErrorMessage(err, 'Could not check the move') }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPlan, moveAsks]);

  // Plan the groups when the window opens on Edit.
  useEffect(() => {
    if (!open || op !== 'edit' || step !== 'choose') return;
    let cancelled = false;
    setEditPlanLoading(true); setEditPlanError(null);
    eachSlice(keys, slice => api.planBulkEdit(slice))
      .then(plans => { if (!cancelled) setEditPlan(mergeEditPlans(plans)); })
      .catch(e => { if (!cancelled) setEditPlanError(extractErrorMessage(e, 'Could not prepare the edit')); })
      .finally(() => { if (!cancelled) setEditPlanLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, op, keys]);

  // Plan the delete as soon as the window opens on it — and again when the
  // selection grows (children added). Nothing is written until Confirm.
  useEffect(() => {
    if (!open || op !== 'delete' || step !== 'choose') return;
    let cancelled = false;
    setDeletePlanLoading(true); setDeletePlanError(null);
    eachSlice(keys, slice => api.planBulkDelete(slice, keys))
      .then(plans => { if (!cancelled) setDeletePlan(mergeDeletePlans(plans)); })
      .catch(e => { if (!cancelled) setDeletePlanError(extractErrorMessage(e, 'Could not check the delete')); })
      .finally(() => { if (!cancelled) setDeletePlanLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, op, keys]);

  // A chosen parent is checked against the hierarchy for that group at once.
  // An answer that arrives after the person picked something else is dropped.
  const parentAsks = JSON.stringify(Object.entries(edits).map(([id, e]) => [id, e.parent?.mode, e.parent?.issue?.key || null]));
  useEffect(() => {
    if (!editPlan) return;
    const askOf = (p) => (p ? `${p.mode}:${p.mode === 'set' ? (p.issue?.key || '') : ''}` : '');
    for (const g of familiesOf(editPlan.groups)) {
      const p = edits[g.id]?.parent;
      if (edits[g.id]?.move) continue; // a move checks its parent in the target project
      if (!p || p.plan || p.planLoading || p.planError) continue;
      if (p.mode === 'set' && !p.issue) continue;
      const ask = askOf(p);
      const parentKey = p.mode === 'set' ? p.issue.key : null;
      const settle = (patch) => setEdits(prev => {
        const cur = prev[g.id]?.parent;
        if (!cur || askOf(cur) !== ask) return prev;
        return { ...prev, [g.id]: { ...prev[g.id], parent: { ...cur, planLoading: false, ...patch } } };
      });
      setEdits(prev => ({ ...prev, [g.id]: { ...prev[g.id], parent: { ...prev[g.id].parent, planLoading: true } } }));
      eachSlice(g.editableKeys, slice => api.planBulkParent(slice, parentKey))
        .then(plans => settle({ plan: mergeParentPlans(plans) }))
        .catch(e => settle({ planError: extractErrorMessage(e, 'Could not check the parent') }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPlan, parentAsks]);

  if (!open) return null;

  // One card per project + work type; the statuses are inside each card.
  const groups = familiesOf(editPlan?.groups || []);
  const editOf = (g) => edits[g.id] || blankEdit();
  const writesOf = (g) => {
    const e = editOf(g);
    // Moving: only fields the target screen offers are written (the rows
    // switch to it); a value chosen for a field it lacks is dropped.
    const offered = e.move?.plan?.targetFields ? new Set(e.move.plan.targetFields.map(f => f.id)) : null;
    const scoped = offered ? { ...e, fields: (e.fields || []).filter(r => offered.has(r.field.id)) } : e;
    return groupWrites({ ...scoped, transition: null }, (field, value) => valueForApi(field, classifyField(field), value));
  };
  const changedGroups = groups.filter(g => hasWrites(writesOf(g)) || chosenTransitions(g, editOf(g)).length > 0);
  const problems = groups.flatMap(g => problemsOf(g, editOf(g)).map(text => ({ group: g, text })));
  const deleteCount = deletePlan ? deletePlan.deletable.length : 0;
  const editCount = changedGroups.reduce((n, g) => n + touchedKeys(g, editOf(g), writesOf(g)).length, 0);

  const canContinue = !busy && (op === 'delete'
    ? (!deletePlanLoading && deleteCount > 0 && (deletePlan.blocked.length === 0 || skipBlocked))
    : (!editPlanLoading && groups.length > 0));

  // Next never sits greyed out without a reason: it checks, and says what is
  // missing (opening the group it is in), or that nothing changes yet.
  const goNext = () => {
    if (op === 'edit') {
      if (problems.length) {
        setAttention(new Set(problems.map(p => p.group.id)));
        setError(problems.map(p => `${p.group.workTypeName || 'Work item'} · ${p.group.projectKey}: ${p.text}`).join('\n'));
        return;
      }
      if (!changedGroups.length) { setError('Nothing changes yet — fill in at least one row in a group.'); return; }
    }
    setError(null); setAttention(new Set()); setStep('confirm');
  };

  const run = async () => {
    setStep('running'); setBusy(true); setError(null); setProgress(null);
    const reasonOf = (e) => extractErrorMessage(e, 'Request failed');
    try {
      if (op === 'delete') {
        const res = await runInChunks(deletePlan.deletable,
          (slice) => api.bulkDeleteIssues(slice, siteUrl, { selection: deletePlan.deletable, skipBlocked: true }),
          { onProgress: (done, total) => setProgress({ done, total }), reasonOf });
        res.requested += deletePlan.blocked.length + deletePlan.refused.length;
        res.failed.push(
          ...deletePlan.blocked.map(b => ({ key: b.key, reason: `Left out — ${b.children.length} child${b.children.length !== 1 ? 'ren' : ''} not selected.` })),
          ...deletePlan.refused,
        );
        setResult({ op: 'delete', ...res });
        if (typeof onComplete === 'function') onComplete(res, 'delete');
      } else {
        // Per card, in order: project, fields, parent, then status — a
        // transition's validators then see the new values.
        const out = { fields: 0, parent: 0, status: 0, alreadyThere: 0, failed: [] };
        let done = 0;
        const total = editCount;
        for (const g of changedGroups) {
          const w = writesOf(g);
          const label = `${g.workTypeName || 'Work item'} · ${g.projectKey}`;
          const tag = (what, list) => list.map(f => ({ key: f.key, reason: `${what}: ${f.reason}`, group: label }));
          // A move first: the items get their new keys, and the fields below
          // are written on those.
          // Field, parent and project writes reach the items whose status
          // lets this person edit them; a status change reaches its status.
          let groupKeys = g.editableKeys;
          if (w.move) {
            const renamed = new Map();
            const r = await runInChunks(g.editableKeys, async (slice) => {
              const res = await api.bulkMoveIssues(slice, w.move.targetProjectKey, { workTypeId: w.move.workTypeId, statusId: w.move.statusId, parentKey: w.move.parentKey });
              for (const m of res?.moved || []) renamed.set(m.from, m.to);
              return res;
            }, { reasonOf });
            if (w.move.sameProject) {
              out.retyped = (out.retyped || 0) + renamed.size;
              out.failed.push(...tag('Work type', r.failed));
            } else {
              out.moved = (out.moved || 0) + renamed.size;
              out.movedPairs = [...(out.movedPairs || []), ...renamed.entries()];
              out.failed.push(...tag('Project', r.failed));
            }
            groupKeys = g.editableKeys.filter(k => renamed.has(k)).map(k => renamed.get(k));
          }
          if (w.fields && groupKeys.length) {
            const r = await runInChunks(groupKeys, slice => api.bulkEditIssues(slice, w.fields, siteUrl), { reasonOf });
            out.fields += r.processed; out.failed.push(...tag('Fields', r.failed));
          }
          if (w.parentKey !== undefined) {
            const r = await runInChunks(g.editableKeys, slice => api.bulkSetParent(slice, w.parentKey), { reasonOf });
            out.parent += r.processed; out.alreadyThere += r.alreadyThere.length; out.failed.push(...tag('Parent', r.failed));
          }
          for (const { status, transition: t } of chosenTransitions(g, editOf(g))) {
            const body = { transitionId: t.transitionId, toStatusId: t.toStatusId, fields: t.fields || undefined, comment: t.comment || undefined };
            const r = await runInChunks(status.keys, slice => api.bulkTransitionIssues([{ ...body, keys: slice }]), { reasonOf });
            out.status += r.processed; out.alreadyThere += r.alreadyThere.length; out.failed.push(...tag(`Status (${status.statusName})`, r.failed));
          }
          done += touchedKeys(g, editOf(g), w).length; setProgress({ done, total });
        }
        const processed = out.fields + out.parent + out.status + (out.moved || 0) + (out.retyped || 0);
        setResult({ op: 'edit', requested: total, processed, ...out });
        if (typeof onComplete === 'function') onComplete({ processed }, 'edit');
      }
      setStep('result');
    } catch (err) {
      setError(extractErrorMessage(err, op === 'delete' ? 'Delete failed' : 'Bulk edit failed'));
      setStep('choose');
    } finally {
      setBusy(false);
    }
  };

  const items = (k) => `${k} work item${k !== 1 ? 's' : ''}`;
  const summaryLines = op === 'delete'
    ? [`Delete ${items(deleteCount)}.`,
       ...(deletePlan?.blocked?.length ? [`Leave ${items(deletePlan.blocked.length)} with children where they are.`] : []),
       ...(deletePlan?.refused?.length ? [`Leave ${items(deletePlan.refused.length)} you may not delete.`] : [])]
    : changedGroups.map(g => describeGroup(g, editOf(g)));

  return (
    <div className="bulk-edit-overlay" onClick={busy ? undefined : onClose}>
      <div className={`bulk-edit-modal${op === 'edit' ? ' is-edit' : ''}`} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="bulk-edit-header">
          <div>
            <h2 className="bulk-edit-title">
              {op === 'delete' ? 'Delete' : 'Edit'} <span className="bulk-edit-title-count">{items(count)}</span>
            </h2>
            <button type="button" className="bulk-see-list" onClick={() => setShowList(v => !v)} aria-expanded={showList}>
              {showList ? 'Hide the list' : 'See the list'}
            </button>
          </div>
          <button className="bulk-edit-close" onClick={busy ? undefined : onClose} disabled={busy} aria-label="Close">×</button>
        </div>

        {showList && (
          <div className="bulk-selection-list" role="list">
            {keys.slice(0, 300).map(k => (
              <div key={k} className="bulk-selection-row" role="listitem">
                <span className="bulk-parent-key">{k}</span>
                <span className="bulk-parent-summary">{itemsByKey?.get?.(k)?.fields?.summary || ''}</span>
              </div>
            ))}
            {keys.length > 300 && <div className="bulk-edit-incompatible-more">…and {keys.length - 300} more</div>}
          </div>
        )}

        {step === 'choose' && (
          <div className="bulk-edit-body">
            {op === 'edit' && (
              <>
                {editPlanLoading && <div className="bulk-edit-value-hint">Grouping the work items and reading what each group may change…</div>}
                {editPlanError && <div className="bulk-edit-error">{editPlanError}</div>}
                {editPlan && groups.length > 1 && (
                  <p className="bulk-edit-value-hint-small">
                    Grouped by project and work type — open a group to change it. Each status in a group offers its own status changes. Fill in only what should change; anything left as is stays as it is.
                  </p>
                )}
                {groups.map(g => (
                  <GroupCard
                    key={g.id} group={g} edit={editOf(g)} selectedSet={selectedSet}
                    collapsible={groups.length > 1} startOpen={groups.length === 1} disabled={busy}
                    attention={attention.has(g.id)}
                    projects={projects}
                    onEdit={(e) => setEdits(prev => ({ ...prev, [g.id]: e }))}
                    onFillScreen={(sub, transition) => setScreenFor({ group: sub, transition, familyId: g.id })}
                  />
                ))}
                {editPlan?.unavailable?.length > 0 && (
                  <div className="bulk-change-unavailable">
                    <div className="bulk-edit-value-hint-small">{items(editPlan.unavailable.length)} can't be edited and will be left alone:</div>
                    <div className="bulk-edit-incompatible-list">
                      {editPlan.unavailable.slice(0, 20).map(x => (
                        <div key={x.key} className="bulk-edit-incompatible-row">
                          <span className="bulk-edit-incompatible-key">{x.key}</span>
                          <span className="bulk-edit-incompatible-reason">{x.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {op === 'delete' && (
              <div className="bulk-edit-section">
                <DeletePlanView
                  plan={deletePlan} loading={deletePlanLoading} error={deletePlanError}
                  onAddChildren={onAddKeys ? (more) => { setSkipBlocked(false); onAddKeys(more); } : null}
                  skipBlocked={skipBlocked} onSkipBlocked={setSkipBlocked}
                  disabled={busy}
                />
              </div>
            )}

            {error && <div className="bulk-edit-error">{error}</div>}
            <div className="bulk-edit-footer">
              <button className="bulk-edit-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="bulk-edit-btn-primary" onClick={goNext} disabled={!canContinue}>Next</button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="bulk-edit-body">
            <div className="bulk-change-confirm">
              {op === 'edit' ? (
                <>
                  <div className="bulk-confirm-lead">
                    {items(editCount)} in {changedGroups.length} group{changedGroups.length !== 1 ? 's' : ''} will change.
                    {groups.length > changedGroups.length && ` ${groups.length - changedGroups.length} group${groups.length - changedGroups.length !== 1 ? 's stay' : ' stays'} as ${groups.length - changedGroups.length !== 1 ? 'they are' : 'it is'}.`}
                  </div>
                  {changedGroups.map(g => <ConfirmGroup key={g.id} group={g} edit={editOf(g)} />)}
                </>
              ) : (
                <>
                  <div className="bulk-edit-label">About to</div>
                  <ul className="bulk-change-summary">
                    {summaryLines.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </>
              )}
              <div className="bulk-edit-value-hint-small">
                {op === 'delete'
                  ? 'Everything about them goes with them — fields, comments, attachments, links, History. If one was deleted by mistake, ask a site admin.'
                  : 'Each change is recorded in the work item’s History. A status change runs through the workflow as a single one would — validators and post-functions included.'}
              </div>
            </div>
            <div className="bulk-edit-footer">
              <button className="bulk-edit-btn-secondary" onClick={() => setStep('choose')} disabled={busy}>Back</button>
              <button className={op === 'delete' ? 'bulk-edit-btn-danger' : 'bulk-edit-btn-primary'} onClick={run} disabled={busy}>
                {op === 'delete' ? `Delete ${deleteCount}` : `Apply to ${editCount}`}
              </button>
            </div>
          </div>
        )}

        {step === 'running' && (
          <div className="bulk-edit-body bulk-edit-body-centered">
            <div className="bulk-edit-spinner" />
            <p>{op === 'delete' ? `Deleting ${items(deleteCount)}…` : `Updating ${items(editCount)}…`}</p>
            {progress && progress.total > 0 && (
              <div className="bulk-progress" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done}>
                <div className="bulk-progress-bar" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
                <span className="bulk-progress-label">{progress.done} of {progress.total}</span>
              </div>
            )}
          </div>
        )}

        {step === 'result' && result && (
          <div className="bulk-edit-body">
            <div className="bulk-edit-result-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h3 className="bulk-edit-result-title">
              {result.op === 'delete'
                ? `${result.processed} of ${result.requested} deleted`
                : [result.moved ? `${result.moved} moved` : null, result.retyped ? `work type changed on ${result.retyped}` : null, result.fields ? `Fields updated on ${result.fields}` : null, result.parent ? `parent set on ${result.parent}` : null, result.status ? `status changed on ${result.status}` : null].filter(Boolean).join(' · ') || 'Nothing changed'}
            </h3>
            {result.op === 'edit' && result.movedPairs?.length > 0 && (
              <p className="bulk-edit-result-sub">New keys: {result.movedPairs.slice(0, 12).map(([a, b]) => `${a} → ${b}`).join(', ')}{result.movedPairs.length > 12 ? ` and ${result.movedPairs.length - 12} more` : ''}. The old keys still open them.</p>
            )}
            {result.op === 'edit' && result.alreadyThere > 0 && (
              <p className="bulk-edit-result-sub">{result.alreadyThere} were already as asked — left as they were.</p>
            )}
            {result.failed?.length > 0 && (
              <>
                <p className="bulk-edit-result-sub">{result.failed.length} change{result.failed.length !== 1 ? 's' : ''} could not be made:</p>
                <div className="bulk-edit-incompatible-list">
                  {result.failed.slice(0, 30).map((x, i) => (
                    <div key={`${x.key}-${i}`} className="bulk-edit-incompatible-row">
                      <span className="bulk-edit-incompatible-key">{x.key}</span>
                      <span className="bulk-edit-incompatible-reason">{x.reason}</span>
                    </div>
                  ))}
                  {result.failed.length > 30 && <div className="bulk-edit-incompatible-more">…and {result.failed.length - 30} more</div>}
                </div>
              </>
            )}
            <div className="bulk-edit-footer">
              <button className="bulk-edit-btn-primary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>

      {screenFor && (
        <TransitionScreenModal
          issueKey={screenFor.group.keys[0]}
          transition={{ id: screenFor.transition.transitionId, name: screenFor.transition.name, to: { id: screenFor.transition.toStatusId, name: screenFor.transition.toName }, hasScreen: true }}
          bulkCount={screenFor.group.keys.length}
          onClose={() => setScreenFor(null)}
          onSubmit={async (fields, comment) => {
            const fid = screenFor.familyId;
            const sid = screenFor.group.id;
            setEdits(prev => {
              const cur = prev[fid] || blankEdit();
              const ts = cur.transitions || {};
              if (!ts[sid]) return prev;
              return { ...prev, [fid]: { ...cur, transitions: { ...ts, [sid]: { ...ts[sid], fields, comment } } } };
            });
            setScreenFor(null);
          }}
        />
      )}
    </div>
  );
}
